import { getCurrentScope, computed, reactive, watch, onScopeDispose, toRefs, readonly, unref } from 'vue-demi';
import { useQueryClient } from './useQueryClient.esm.js';
import { updateState, shouldThrowError, isQueryKey, cloneDeepUnref } from './utils.esm.js';

function useBaseQuery(Observer, arg1, arg2 = {}, arg3 = {}) {
  var _options$value$queryC;

  if (process.env.NODE_ENV === 'development') {
    if (!getCurrentScope()) {
      console.warn('vue-query composables like "uesQuery()" should only be used inside a "setup()" function or a running effect scope. They might otherwise lead to memory leaks.');
    }
  }

  const options = computed(() => parseQueryArgs(arg1, arg2, arg3));
  const queryClient = (_options$value$queryC = options.value.queryClient) != null ? _options$value$queryC : useQueryClient(options.value.queryClientKey);
  const defaultedOptions = computed(() => {
    const defaulted = queryClient.defaultQueryOptions(options.value);
    defaulted._optimisticResults = queryClient.isRestoring.value ? 'isRestoring' : 'optimistic';
    return defaulted;
  });
  const observer = new Observer(queryClient, defaultedOptions.value);
  const state = reactive(observer.getCurrentResult());

  let unsubscribe = () => {// noop
  };

  watch(queryClient.isRestoring, isRestoring => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!isRestoring) {
      unsubscribe();
      unsubscribe = observer.subscribe(result => {
        updateState(state, result);
      });
    }
  }, {
    immediate: true
  });
  watch(defaultedOptions, () => {
    observer.setOptions(defaultedOptions.value);
    updateState(state, observer.getCurrentResult());
  }, {
    flush: 'sync'
  });
  onScopeDispose(() => {
    unsubscribe();
  });

  const suspense = () => {
    return new Promise((resolve, reject) => {
      let stopWatch = () => {//noop
      };

      const run = () => {
        if (defaultedOptions.value.enabled !== false) {
          const optimisticResult = observer.getOptimisticResult(defaultedOptions.value);

          if (optimisticResult.isStale) {
            stopWatch();
            observer.fetchOptimistic(defaultedOptions.value).then(resolve, reject);
          } else {
            stopWatch();
            resolve(optimisticResult);
          }
        }
      };

      run();
      stopWatch = watch(defaultedOptions, run);
    });
  }; // Handle error boundary


  watch(() => state.error, error => {
    if (state.isError && !state.isFetching && shouldThrowError(defaultedOptions.value.useErrorBoundary, [error, observer.getCurrentQuery()])) {
      throw error;
    }
  });
  return { ...toRefs(readonly(state)),
    suspense
  };
}
function parseQueryArgs(arg1, arg2 = {}, arg3 = {}) {
  const plainArg1 = unref(arg1);
  const plainArg2 = unref(arg2);
  const plainArg3 = unref(arg3);
  let options = plainArg1;

  if (!isQueryKey(plainArg1)) {
    options = plainArg1;
  } else if (typeof plainArg2 === 'function') {
    options = { ...plainArg3,
      queryKey: plainArg1,
      queryFn: plainArg2
    };
  } else {
    options = { ...plainArg2,
      queryKey: plainArg1
    };
  }

  return cloneDeepUnref(options);
}

export { parseQueryArgs, useBaseQuery };
//# sourceMappingURL=useBaseQuery.esm.js.map
